import React, { Component } from "react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Edit2, Trash2, RefreshCw, X, Save, Search, DollarSign, CreditCard, CheckCircle, Clock, AlertCircle, Banknote, Hotel } from "lucide-react";

const PAYMENT_MODES = ["Cash", "Bank Transfer", "Cheque", "UPI", "Credit Card", "Debit Card", "NEFT", "RTGS", "IMPS", "Other"];

const MODE_COLORS = {
  "Cash":          { bg: "#ECFDF5", color: "#0B7A4C", border: "#A7F3D0" },
  "UPI":           { bg: "#EFF6FF", color: "#1E40AF", border: "#BFDBFE" },
  "Bank Transfer": { bg: "#F5F3FF", color: "#5B21B6", border: "#DDD6FE" },
  "Cheque":        { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
  "Credit Card":   { bg: "#FFF1F2", color: "#9F1239", border: "#FECDD3" },
  "Debit Card":    { bg: "#F0F9FF", color: "#0369A1", border: "#BAE6FD" },
  "NEFT":          { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0" },
  "RTGS":          { bg: "#FDF4FF", color: "#7E22CE", border: "#E9D5FF" },
  "IMPS":          { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  "Other":         { bg: "#F9FAFB", color: "#374151", border: "#E5E7EB" },
};

const fmt = n => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(parseFloat(n) || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

/* ─── Modal ─────────────────────────────────────────────────────────────────── */
class PaymentModal extends Component {
  constructor(props) {
    super(props);
    const p = props.payment;
    this.state = {
      form: {
        booking_id:      p?.booking_id      || "",
        amount:          p?.amount          || "",
        mode_of_payment: p?.mode_of_payment || "",
        reference_no:    p?.reference_no    || "",
        payment_date:    p?.payment_date    ? String(p.payment_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        notes:           p?.notes           || "",
      },
      booking: p ? { guest_name: p.guest_name, hotel_name: p.hotel_name } : null,
      loadingBooking: false, saving: false, errors: {},
    };
    this.bookingSearchTimer = null;
  }

  validate() {
    const { form } = this.state; const errs = {};
    if (!form.booking_id)         errs.booking_id = "Booking ID is required";
    if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = "Valid amount required";
    if (!form.mode_of_payment)    errs.mode_of_payment = "Select payment mode";
    return errs;
  }

  async lookupBooking(id) {
    if (!id) { this.setState({ booking: null }); return; }
    this.setState({ loadingBooking: true });
    try {
      const { data } = await api.get(`/bookings/${id}`);
      const b = data.data;
      this.setState({ booking: b ? { guest_name: b.guest_name, hotel_name: b.hotel_name, total_sale: b.total_sale, conf_no: b.conf_no } : null });
      if (!b) this.setState({ errors: { ...this.state.errors, booking_id: `Booking #${id} not found` } });
    } catch { this.setState({ booking: null, errors: { ...this.state.errors, booking_id: `Booking #${id} not found` } }); }
    finally { this.setState({ loadingBooking: false }); }
  }

  async handleSubmit() {
    const errs = this.validate();
    if (Object.keys(errs).length) { this.setState({ errors: errs }); return; }
    this.setState({ saving: true });
    try {
      if (this.props.payment) {
        await api.put(`/payments/${this.props.payment.id}`, this.state.form);
        toast.success("Payment updated!");
      } else {
        await api.post("/payments", this.state.form);
        toast.success("Payment recorded!");
      }
      this.props.onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { this.setState({ saving: false }); }
  }

  render() {
    const { onClose, payment } = this.props;
    const { form, booking, loadingBooking, saving, errors } = this.state;
    const inp = err => ({ width: "100%", background: err ? "#FFF5F5" : "#F9FAFB", border: `1.5px solid ${err ? "#FCA5A5" : "#D1D5DB"}`, borderRadius: 10, padding: "9px 12px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none" });
    const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 5 };
    const set = (k, v) => this.setState(p => ({ form: { ...p.form, [k]: v }, errors: { ...p.errors, [k]: "" } }));

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 540, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ background: "#16213E", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
              <CreditCard size={16} color="#C9A84C" /> {payment ? "Edit Payment" : "Record Payment"}
            </span>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
          </div>

          {/* Body */}
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Booking ID */}
            <div>
              <label style={lbl}>Booking ID *</label>
              <div style={{ position: "relative" }}>
                <input type="number" value={form.booking_id} placeholder="Enter booking ID (e.g. 42)"
                  onChange={e => {
                    set("booking_id", e.target.value);
                    clearTimeout(this.bookingSearchTimer);
                    this.bookingSearchTimer = setTimeout(() => this.lookupBooking(e.target.value), 600);
                  }}
                  style={{ ...inp(errors.booking_id), paddingRight: 36 }} />
                {loadingBooking && <RefreshCw size={13} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", animation: "spin 1s linear infinite", color: "#9CA3AF" }} />}
              </div>
              {errors.booking_id && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.booking_id}</p>}
              {booking && (
                <div style={{ marginTop: 6, padding: "8px 12px", background: "#F0FDF4", border: "1px solid #A7F3D0", borderRadius: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: "#0B7A4C" }}>✓ {booking.guest_name}</span>
                  {booking.hotel_name && <span style={{ color: "#6B7280" }}> — {booking.hotel_name}</span>}
                  {booking.total_sale && <span style={{ color: "#0FA968", fontWeight: 600, marginLeft: 8 }}>Total: ₹{fmt(booking.total_sale)}</span>}
                </div>
              )}
            </div>

            {/* Amount + Date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={lbl}>Amount (₹) *</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 13, fontWeight: 600 }}>₹</span>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set("amount", e.target.value)}
                    style={{ ...inp(errors.amount), paddingLeft: 26 }} placeholder="0.00" />
                </div>
                {errors.amount && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.amount}</p>}
              </div>
              <div>
                <label style={lbl}>Payment Date *</label>
                <input type="date" value={form.payment_date} onChange={e => set("payment_date", e.target.value)}
                  onClick={e => { try { e.target.showPicker(); } catch(_){} }}
                  style={{ ...inp(false), cursor: "pointer" }} />
              </div>
            </div>

            {/* Mode of Payment */}
            <div>
              <label style={lbl}>Mode of Payment *</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PAYMENT_MODES.map(m => {
                  const active = form.mode_of_payment === m;
                  const clr = MODE_COLORS[m] || MODE_COLORS["Other"];
                  return (
                    <button key={m} type="button" onClick={() => set("mode_of_payment", m)}
                      style={{ padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                        border: active ? `2px solid ${clr.color}` : "1.5px solid #E5E7EB",
                        background: active ? clr.bg : "#fff", color: active ? clr.color : "#374151",
                        boxShadow: active ? "0 2px 6px rgba(0,0,0,0.1)" : "none" }}>
                      {m}
                    </button>
                  );
                })}
              </div>
              {errors.mode_of_payment && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.mode_of_payment}</p>}
            </div>

            {/* Reference No + Notes */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={lbl}>Reference No. (optional)</label>
                <input value={form.reference_no} onChange={e => set("reference_no", e.target.value)}
                  style={inp(false)} placeholder="TXN123 / Cheque No." />
              </div>
              <div>
                <label style={lbl}>Notes (optional)</label>
                <input value={form.notes} onChange={e => set("notes", e.target.value)}
                  style={inp(false)} placeholder="Any remarks..." />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
            <button onClick={() => this.handleSubmit()} disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving ? "#9CA3AF" : "#16213E", fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              {saving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Save size={14} /> {payment ? "Update" : "Record Payment"}</>}
            </button>
          </div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

/* ─── Booking Payment Modal — same rich flow as the Record Payment button on
   the All Bookings page (Transaction ID, mode, full/part toggle, live
   balance), reused here for the Pending Payment tab. Posts to the booking
   payment endpoint so it updates the booking AND lands in the payments
   table. ──────────────────────────────────────────────────────────────── */
const PAYMENT_MODES_RICH = ["UPI", "NEFT", "RTGS", "IMPS", "Cheque", "Cash", "Credit Card", "Debit Card"];

class BookingPaymentModal extends Component {
  constructor(props) {
    super(props);
    const b = props.booking;
    const totalSale   = parseFloat(b.total_sale)     || 0;
    const alreadyPaid = parseFloat(b.payment_amount) || 0;
    const balance     = Math.max(0, totalSale - alreadyPaid);
    this.state = {
      txnId: "", mode: "UPI",
      type:   alreadyPaid > 0 ? "part" : "full",
      amount: alreadyPaid > 0 ? String(balance) : String(totalSale),
      notes: "", saving: false,
      paymentDate: new Date().toISOString().slice(0, 10),
    };
    this.handleSave = this.handleSave.bind(this);
  }

  async handleSave() {
    const { booking, onSaved } = this.props;
    const { mode, type, amount, notes, paymentDate } = this.state;
    // Cash payments don't have a real transaction ID — default it to "0"
    // regardless of whatever is in state, rather than relying on the UI
    // having kept it in sync.
    const txnId = mode === "Cash" ? "0" : this.state.txnId;
    if (!txnId.trim()) { toast.error("Transaction ID is required"); return; }
    if (!mode)          { toast.error("Select a payment mode"); return; }
    if (!paymentDate)   { toast.error("Select a payment date"); return; }
    const totalSale   = parseFloat(booking.total_sale)     || 0;
    const alreadyPaid = parseFloat(booking.payment_amount) || 0;
    const balance     = Math.max(0, totalSale - alreadyPaid);
    const amountPaid  = type === "full" ? balance : parseFloat(amount) || 0;
    if (!amountPaid || amountPaid <= 0) { toast.error("Enter a valid amount"); return; }
    this.setState({ saving: true });
    try {
      await api.post(`/bookings/${booking.id}/payment`, {
        transaction_id: txnId.trim(), payment_mode: mode,
        payment_type: type, amount: amountPaid, notes: notes.trim() || null,
        payment_date: paymentDate,
      });
      toast.success(type === "full" ? "Full payment recorded!" : `Part payment of ₹${amountPaid.toFixed(2)} recorded!`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to record payment"); }
    finally { this.setState({ saving: false }); }
  }

  render() {
    const { booking, onClose } = this.props;
    const { txnId, mode, type, amount, notes, saving, paymentDate } = this.state;
    const totalSale   = parseFloat(booking.total_sale)     || 0;
    const alreadyPaid = parseFloat(booking.payment_amount) || 0;
    const balance     = Math.max(0, totalSale - alreadyPaid);
    const partAmount  = parseFloat(amount) || 0;
    const balanceLeft = type === "full" ? 0 : Math.max(0, balance - partAmount);

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "92vh", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ background: "#0B7A4C", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Banknote size={18} color="#6EE7B7" />
              <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Record Payment</div><div style={{ fontSize: 11, color: "#BEEED9", marginTop: 2 }}>#{booking.id} — {booking.guest_name || booking.hotel_name}</div></div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
          </div>
          <div style={{ padding: "14px 24px", background: "#F0FDF4", borderBottom: "1px solid #D1FAE5", display: "flex", gap: 24 }}>
            <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Hotel</div><div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>{booking.hotel_name}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Sold To</div><div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>{booking.sold_to}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Invoice</div><div style={{ fontSize: 13, fontWeight: 600, color: "#0FA968", marginTop: 2, fontFamily: "JetBrains Mono,monospace" }}>{booking.invoice_no || `₹${fmt(booking.total_sale)}`}</div></div>
          </div>
          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", flex: 1 }}>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Transaction ID *</label>
                <input type="text" placeholder="e.g. UPI123456789" value={mode === "Cash" ? "0" : txnId}
                  onChange={e => mode !== "Cash" && this.setState({ txnId: e.target.value })}
                  readOnly={mode === "Cash"} autoFocus={mode !== "Cash"}
                  style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "JetBrains Mono,monospace", fontSize: 13, color: mode === "Cash" ? "#6B7280" : "#111827", outline: "none", boxSizing: "border-box", background: mode === "Cash" ? "#F3F4F6" : "#fff", cursor: mode === "Cash" ? "not-allowed" : "text" }} />
              </div>
              <div style={{ width: 160 }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Payment Date *</label>
                <input type="date" value={paymentDate} max={new Date().toISOString().slice(0, 10)} onChange={e => this.setState({ paymentDate: e.target.value })}
                  style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "JetBrains Mono,monospace", fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Mode of Payment *</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PAYMENT_MODES_RICH.map(m => (
                  <button key={m} type="button" onClick={() => this.setState({
                    mode: m,
                    // Cash has no real transaction ID — default it to "0". Switching
                    // away from Cash clears it back out so the user enters a real one.
                    txnId: m === "Cash" ? "0" : (mode === "Cash" ? "" : txnId),
                  })}
                    style={{ padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: mode === m ? "none" : "1.5px solid #D1D5DB", background: mode === m ? "#0B7A4C" : "#fff", color: mode === m ? "#fff" : "#374151" }}>{m}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 8 }}>Payment Type *</label>
              <div style={{ display: "flex", border: "1.5px solid #D1D5DB", borderRadius: 10, overflow: "hidden" }}>
                {[["full", "Full Payment"], ["part", "Part Payment"]].map(([t, label]) => (
                  <button key={t} type="button" onClick={() => this.setState({ type: t, amount: t === "full" ? String(balance) : "" })}
                    style={{ flex: 1, padding: "10px 0", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", background: type === t ? (t === "full" ? "#0B7A4C" : "#1D4ED8") : "#fff", color: type === t ? "#fff" : "#6B7280" }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em", marginBottom: 3 }}>Total Sale</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#0B7A4C", fontFamily: "JetBrains Mono,monospace" }}>₹{totalSale.toFixed(2)}</div>
                {alreadyPaid > 0 && <div style={{ fontSize: 11, color: "#0FA968", marginTop: 3 }}>Already paid: ₹{alreadyPaid.toFixed(2)}</div>}
              </div>
              <div style={{ flex: 1, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em", marginBottom: 3 }}>{type === "full" ? "Paying Now" : "Balance After This"}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#D97706", fontFamily: "JetBrains Mono,monospace" }}>₹{(type === "full" ? balance : balanceLeft).toFixed(2)}</div>
                {type === "part" && balanceLeft === 0 && partAmount > 0 && <div style={{ fontSize: 11, color: "#0FA968", marginTop: 3 }}>Fully settled ✓</div>}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>{type === "full" ? "Amount (₹) — Full" : "Part Payment Amount (₹) *"}</label>
              <input type="number" value={type === "full" ? balance.toFixed(2) : amount} readOnly={type === "full"}
                onChange={e => type === "part" && this.setState({ amount: e.target.value })}
                onFocus={e => type === "part" && e.target.select()}
                style={{ width: "100%", border: `1.5px solid ${type === "part" ? "#2F5FE0" : "#E5E7EB"}`, borderRadius: 10, padding: "10px 14px", fontFamily: "JetBrains Mono,monospace", fontSize: 14, fontWeight: 700, color: type === "full" ? "#0B7A4C" : "#111827", outline: "none", boxSizing: "border-box", background: type === "full" ? "#F0FDF4" : "#fff", cursor: type === "full" ? "not-allowed" : "text" }} />
              {type === "part" && partAmount > 0 && balanceLeft > 0 && <p style={{ fontSize: 11, color: "#D97706", marginTop: 4 }}>⚠️ Part payment — ₹{balanceLeft.toFixed(2)} will remain outstanding</p>}
              {type === "part" && partAmount > 0 && balanceLeft === 0 && <p style={{ fontSize: 11, color: "#0FA968", marginTop: 4 }}>✓ This payment will fully settle the invoice</p>}
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Notes (Optional)</label>
              <textarea rows={2} placeholder="Any additional payment notes…" value={notes} onChange={e => this.setState({ notes: e.target.value })}
                style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none", resize: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
            <button type="button" onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
            <button type="button" onClick={this.handleSave} disabled={saving || (mode !== "Cash" && !txnId.trim())}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving || (mode !== "Cash" && !txnId.trim()) ? "#9CA3AF" : "#0B7A4C", fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving || (mode !== "Cash" && !txnId.trim()) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              {saving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Banknote size={14} /> Record Payment</>}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
class PaymentsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      payments: [], total: 0, loading: true, showModal: false, editPayment: null, search: "", filterMode: "",
      activeTab: "records", allBookings: [], bookingsLoading: false, payBookingModal: null,
    };
    this.fetch         = this.fetch.bind(this);
    this.fetchBookings = this.fetchBookings.bind(this);
  }
  componentDidMount() { this.fetch(); this.fetchBookings(); }

  async fetch() {
    this.setState({ loading: true });
    try {
      const { data } = await api.get("/payments", { params: { limit: 1000000 } });
      this.setState({ payments: data.data || [], total: data.total || 0 });
    } catch { toast.error("Failed to load payments"); }
    finally { this.setState({ loading: false }); }
  }

  // Pending Payment tab — pulls the same role-scoped /bookings list used
  // elsewhere (Staff see their own, Admin/Manager/Accounts see everyone's).
  async fetchBookings() {
    this.setState({ bookingsLoading: true });
    try {
      const { data } = await api.get("/bookings", { params: { limit: 1000000 } });
      this.setState({ allBookings: data.data || [] });
    } catch { toast.error("Failed to load bookings"); }
    finally { this.setState({ bookingsLoading: false }); }
  }

  async handleDelete(id) {
    if (!window.confirm("Delete this payment record?")) return;
    try { await api.delete(`/payments/${id}`); toast.success("Deleted"); this.fetch(); }
    catch (e) { toast.error(e.response?.data?.message || "Cannot delete"); }
  }

  render() {
    const { payments, loading, showModal, editPayment, search, filterMode, activeTab, allBookings, bookingsLoading, payBookingModal } = this.state;
    const { user } = this.props;

    // Filters
    const filtered = payments.filter(p => {
      const q = search.toLowerCase();
      const matchSearch = !q || String(p.booking_id).includes(q) || (p.guest_name || "").toLowerCase().includes(q) || (p.hotel_name || "").toLowerCase().includes(q) || (p.reference_no || "").toLowerCase().includes(q);
      const matchMode = !filterMode || p.mode_of_payment === filterMode;
      return matchSearch && matchMode;
    });

    // Stats
    const totalAmount    = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const todayPayments  = payments.filter(p => String(p.payment_date || "").slice(0, 10) === new Date().toISOString().slice(0, 10));
    const todayAmount    = todayPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const modeBreakdown  = PAYMENT_MODES.map(m => ({ mode: m, count: payments.filter(p => p.mode_of_payment === m).length })).filter(m => m.count > 0);

    // Pending Payment tab — show every booking here; the Record Payment
    // action itself is only enabled once a booking is Invoiced (and still
    // has a balance owing) since that's when payment collection applies.
    const outstandingBookings = allBookings.filter(b => {
      const sale = parseFloat(b.total_sale)     || 0;
      const paid = parseFloat(b.payment_amount) || 0;
      return sale - paid > 0.01;
    });
    const pendingTotal = outstandingBookings.reduce((s, b) => {
      const sale = parseFloat(b.total_sale)     || 0;
      const paid = parseFloat(b.payment_amount) || 0;
      return s + Math.max(0, sale - paid);
    }, 0);
    // Full list, minus Cancelled and anything with nothing left to collect —
    // a fully-settled booking has no place in a "Pending Payment" list.
    const pendingBookings = allBookings.filter(b => {
      if (b.status === "Cancelled") return false;
      const sale = parseFloat(b.total_sale)     || 0;
      const paid = parseFloat(b.payment_amount) || 0;
      return sale - paid > 0;
    });

    return (
      <div>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#16213E", letterSpacing: "-0.02em" }}>
              Payment <span style={{ color: "#FF6B4A" }}>Records</span>
            </h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>{payments.length} records · ₹{fmt(totalAmount)} total collected</p>
          </div>
          {activeTab === "records" && (
            <button onClick={() => this.setState({ showModal: true, editPayment: null })}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 22px", borderRadius: 12, border: "none", background: "#FF6B4A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(255,107,74,0.28)" }}>
              <Plus size={16} /> Record Payment
            </button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1.5px solid #E5E7EB" }}>
          {[
            ["records", "Payment Records", CreditCard, null],
            ["pending", "Pending Payment", AlertCircle, outstandingBookings.length],
          ].map(([key, label, Icon, count]) => (
            <button key={key} onClick={() => this.setState({ activeTab: key })}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 16px", border: "none", borderBottom: activeTab === key ? "2.5px solid #FF6B4A" : "2.5px solid transparent", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: activeTab === key ? "#16213E" : "#9CA3AF", marginBottom: -1.5 }}>
              <Icon size={14} /> {label}
              {count > 0 && (
                <span style={{ fontSize: 10, fontWeight: 800, color: "#fff", background: key === "pending" ? "#D97706" : "#2F5FE0", borderRadius: 20, padding: "1px 7px" }}>{count}</span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "pending" ? (
          <>
            {/* Pending stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 }}>
              <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 14, padding: "16px 20px" }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>⏳</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#D97706" }}>₹{fmt(pendingTotal)}</div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Total Outstanding</div>
                <div style={{ fontSize: 11, color: "#D97706", fontWeight: 600 }}>{outstandingBookings.length} booking{outstandingBookings.length !== 1 ? "s" : ""} pending</div>
              </div>
            </div>

            {/* Pending table — every booking; Record Payment is only enabled once Invoiced */}
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
              {bookingsLoading ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
                  <RefreshCw size={22} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} />
                  <p>Loading bookings…</p>
                </div>
              ) : pendingBookings.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
                  <CheckCircle size={36} style={{ marginBottom: 12, opacity: 0.4, color: "#0FA968" }} />
                  <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>Nothing pending</p>
                  <p style={{ fontSize: 13, marginTop: 4 }}>Every booking has been fully paid</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {["#", "Guest", "Hotel", "Sold To", "Status", "Total Sale", "Paid", "Balance", ""].map(h => (
                          <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF", borderBottom: "1.5px solid #F3F4F6", background: "#FAFBFF", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingBookings.map((b, i) => {
                        const sale      = parseFloat(b.total_sale)     || 0;
                        const paid      = parseFloat(b.payment_amount) || 0;
                        const balance   = Math.max(0, sale - paid);
                        const isPartPaid = paid > 0 && paid < sale;
                        // Any booking that isn't fully settled yet stays payable —
                        // don't disable it just because the remaining balance is
                        // small; only a genuinely fully-paid booking (balance <= 0)
                        // should turn the button off.
                        const canPay    = b.status === "Invoiced" && balance > 0;
                        return (
                          <tr key={b.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}>
                            <td style={{ padding: "12px 16px", fontSize: 11, color: "#9CA3AF", fontFamily: "JetBrains Mono,monospace", borderBottom: "1px solid #F3F4F6" }}>#{b.id}</td>
                            <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827", borderBottom: "1px solid #F3F4F6" }}>{b.guest_name || "—"}</td>
                            <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6" }}>{b.hotel_name}</td>
                            <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6" }}>{b.sold_to}</td>
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: b.status === "Invoiced" ? "#5B21B6" : "#B7791F", background: b.status === "Invoiced" ? "#F5F3FF" : "#FFFBEB", border: `1px solid ${b.status === "Invoiced" ? "#DDD6FE" : "#FDE68A"}`, borderRadius: 20, padding: "2px 9px", whiteSpace: "nowrap" }}>{b.status}</span>
                              {isPartPaid && <div style={{ fontSize: 10, color: "#D97706", marginTop: 3 }}>Part paid</div>}
                            </td>
                            <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 13, fontWeight: 600, color: "#111827", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>₹{fmt(sale)}</td>
                            <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 13, color: "#0FA968", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>₹{fmt(paid)}</td>
                            <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 14, fontWeight: 800, color: balance > 0 ? "#D97706" : "#0FA968", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>₹{fmt(balance)}</td>
                            <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                              <button onClick={() => canPay && this.setState({ payBookingModal: b })} disabled={!canPay}
                                title={!canPay ? (balance <= 0 ? "Fully paid" : "Available once booking is Invoiced") : undefined}
                                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: `1px solid ${canPay ? "rgba(5,150,105,0.25)" : "#E5E7EB"}`, background: canPay ? "rgba(5,150,105,0.08)" : "#F9FAFB", color: canPay ? "#0FA968" : "#9CA3AF", fontSize: 12, fontWeight: 700, cursor: canPay ? "pointer" : "not-allowed", whiteSpace: "nowrap", opacity: canPay ? 1 : 0.7 }}>
                                <Banknote size={13} /> Record Payment
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#16213E" }}>
                        <td colSpan={7} style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#C9A84C", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                          {pendingBookings.length} Pending Booking{pendingBookings.length !== 1 ? "s" : ""}
                        </td>
                        <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 15, fontWeight: 800, color: "#FDBA74" }}>₹{fmt(pendingTotal)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 }}>
          {[
            { label: "Total Collected", value: `₹${fmt(totalAmount)}`, sub: `${payments.length} records`, icon: "💰", bg: "#F0FDF4", border: "#A7F3D0", color: "#0B7A4C" },
            { label: "Today's Collection", value: `₹${fmt(todayAmount)}`, sub: `${todayPayments.length} payment${todayPayments.length !== 1 ? "s" : ""}`, icon: "📅", bg: "#EFF6FF", border: "#BFDBFE", color: "#1E40AF" },
            { label: "Unique Bookings", value: [...new Set(payments.map(p => p.booking_id))].length, sub: "bookings paid", icon: "📋", bg: "#F5F3FF", border: "#DDD6FE", color: "#5B21B6" },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 14, padding: "16px 20px" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
            <input placeholder="Search booking ID, guest, hotel..." value={search}
              onChange={e => this.setState({ search: e.target.value })}
              style={{ width: "100%", paddingLeft: 34, padding: "9px 12px 9px 34px", border: "1.5px solid #E5E7EB", borderRadius: 10, fontSize: 13, outline: "none", fontFamily: "Sora,sans-serif", boxSizing: "border-box" }} />
          </div>
          <select value={filterMode} onChange={e => this.setState({ filterMode: e.target.value })}
            style={{ height: 38, border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "0 12px", fontSize: 13, fontFamily: "Sora,sans-serif", outline: "none", background: "#fff" }}>
            <option value="">All Modes</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {(search || filterMode) && (
            <button onClick={() => this.setState({ search: "", filterMode: "" })}
              style={{ height: 38, padding: "0 14px", border: "1px solid #E5E7EB", borderRadius: 10, background: "#F9FAFB", fontSize: 12, color: "#6B7280", cursor: "pointer" }}>
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
              <RefreshCw size={22} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} />
              <p>Loading payments…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
              <CreditCard size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>{payments.length === 0 ? "No payments recorded yet" : "No results found"}</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    {["#", "Date", "Booking", "Guest / Hotel", "Mode", "Reference", "Amount", "Recorded By", "Actions"].map(h => (
                      <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF", borderBottom: "1.5px solid #F3F4F6", background: "#FAFBFF", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const clr = MODE_COLORS[p.mode_of_payment] || MODE_COLORS["Other"];
                    return (
                      <tr key={p.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}>
                        <td style={{ padding: "12px 16px", fontSize: 11, color: "#9CA3AF", fontFamily: "JetBrains Mono,monospace", borderBottom: "1px solid #F3F4F6" }}>#{p.id}</td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{fmtDate(p.payment_date)}</td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 12, fontWeight: 700, color: "#16213E", background: "#EEF2FF", padding: "2px 8px", borderRadius: 6 }}>
                            #{p.booking_id}
                          </span>
                          {p.conf_no && <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>Conf: {p.conf_no}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{p.guest_name || "—"}</div>
                          {p.hotel_name && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{p.hotel_name}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: clr.bg, color: clr.color, border: `1px solid ${clr.border}`, whiteSpace: "nowrap" }}>
                            {p.mode_of_payment}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", borderBottom: "1px solid #F3F4F6" }}>
                          {p.reference_no ? <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 11 }}>{p.reference_no}</span> : <span style={{ color: "#D1D5DB" }}>—</span>}
                          {p.notes && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{p.notes}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 14, fontWeight: 800, color: "#0B7A4C" }}>₹{fmt(p.amount)}</span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6" }}>{p.recorded_by || "—"}</td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => this.setState({ showModal: true, editPayment: p })}
                              style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                              <Edit2 size={12} />
                            </button>
                            <button onClick={() => this.handleDelete(p.id)}
                              style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #F5CAC5", background: "#FCEAE8", color: "#E1483A", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Total footer */}
                <tfoot>
                  <tr style={{ background: "#16213E" }}>
                    <td colSpan={6} style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#C9A84C", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {filtered.length} Record{filtered.length !== 1 ? "s" : ""}
                    </td>
                    <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 15, fontWeight: 800, color: "#fff" }}>
                      ₹{fmt(filtered.reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {showModal && (
          <PaymentModal payment={editPayment}
            onClose={() => this.setState({ showModal: false, editPayment: null })}
            onSaved={() => { this.setState({ showModal: false, editPayment: null }); this.fetch(); }} />
        )}
        {payBookingModal && (
          <BookingPaymentModal booking={payBookingModal}
            onClose={() => this.setState({ payBookingModal: null })}
            onSaved={() => { this.setState({ payBookingModal: null }); this.fetchBookings(); this.fetch(); }} />
        )}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}
export default PaymentsPage;